import { Button, Col, Input, Row, Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { BsPerson } from 'react-icons/bs';
import { LuSearch } from 'react-icons/lu';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { BankListOptions } from 'constants/banksList';
import {
	DestinationKeyTypeDescription,
	DestinationTypeDescription,
} from 'constants/destination';
import { DestinationKeyType, DestinationType } from 'types/Destination';
import dayjs from 'dayjs';
import { CustomDrawer } from 'components/CustomDrawer';
import { CustomRangePicker } from 'components/CustomRangePicker';
import { serializeOnlyNumbers } from 'helpers/serializers';
import { EscrowDestinationsFiltersType } from './EscrowDetailsAccountDestination.types';
import {
	FiltersForm,
	FiltersFormItem,
} from './EscrowDetailsAccountDestination.styles';

interface IEscrowDetailsAccountDestinationFiltersProps {
	isOpen: boolean;
	filters?: EscrowDestinationsFiltersType;
	onClose: () => void;
	onApply: (filters: EscrowDestinationsFiltersType) => void;
}

const EscrowDetailsAccountDestinationFilters = ({
	isOpen,
	filters,
	onApply,
	onClose,
}: IEscrowDetailsAccountDestinationFiltersProps) => {
	const [form] = FiltersForm.useForm();

	const onFinish = useCallback(
		(values: EscrowDestinationsFiltersType) => {
			onApply({
				id:
					values.id && values.id.length > 0
						? values.id.trim()
						: undefined,
				account:
					values.account && values.account.length > 0
						? serializeOnlyNumbers(values.account)
						: undefined,
				creator:
					values.creator && values.creator.length > 0
						? values.creator
						: undefined,
				bank: values.bank || undefined,
				branch:
					values.branch && values.branch.length > 0
						? serializeOnlyNumbers(values.branch)
						: undefined,
				type: values.type || undefined,
				key_type: values.key_type || undefined,
				created_at_start_date: values.created_at_start_date
					? values.created_at_start_date
					: undefined,
				created_at_end_date: values.created_at_end_date
					? values.created_at_end_date
					: undefined,
			});
		},
		[onApply],
	);

	useEffect(() => {
		if (isOpen) {
			form.setFieldsValue({
				id: filters?.id,
				account: filters?.account,
				creator: filters?.creator,
				bank: filters?.bank,
				branch: filters?.branch,
				type: filters?.type,
				key_type: filters?.key_type,
				created_at_start_date: filters?.created_at_start_date,
				created_at_end_date: filters?.created_at_end_date,
			});
		}
	}, [isOpen, form, filters]);

	return (
		<CustomDrawer
			onClose={onClose}
			open={isOpen}
			title="Busca avançada"
			footer={
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<Button
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								justifyContent: 'center',
								position: 'relative',
							}}
							size="large"
							type="primary"
							onClick={form.submit}
						>
							Aplicar filtros
							<LuSearch
								size={21}
								style={{ position: 'absolute', right: '15px' }}
							/>
						</Button>
					</Col>
				</Row>
			}
		>
			<FiltersForm
				initialValues={{
					id: filters?.id,
					account: filters?.account,
					creator: filters?.creator,
					bank: filters?.bank,
					branch: filters?.branch,
					type: filters?.type,
					key_type: filters?.key_type,
					created_at_start_date: filters?.created_at_start_date,
					created_at_end_date: filters?.created_at_end_date,
				}}
				layout="vertical"
				form={form}
				onFinish={onFinish}
			>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem label="ID" name="id">
							<Input
								placeholder="Digite aqui"
								prefix={<LuSearch />}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem label="Nº da conta" name="account">
							<Input
								placeholder="Digite aqui"
								prefix={<MdOutlineAccountBalanceWallet />}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem name="bank" label="Banco">
							<Select
								showSearch={true}
								allowClear={true}
								placeholder="Selecionar"
								filterOption={(input, option) =>
									!!option?.label
										?.toLowerCase()
										?.includes(input.toLowerCase())
								}
								options={BankListOptions}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem name="branch" label="Agência">
							<Input
								placeholder="Digite aqui"
								prefix={<MdOutlineAccountBalanceWallet />}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem name="type" label="Tipo de conta">
							<Select
								allowClear={true}
								placeholder="Selecionar"
								options={Object.keys(
									DestinationTypeDescription,
								).map(key => ({
									label: DestinationTypeDescription[
										key as DestinationType
									],
									value: key,
								}))}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem name="key_type" label="Chave Pix">
							<Select
								allowClear={true}
								placeholder="Selecionar"
								options={Object.keys(
									DestinationKeyTypeDescription,
								).map(key => ({
									label: DestinationKeyTypeDescription[
										key as DestinationKeyType
									],
									value: key,
								}))}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem
							name="creator"
							label="Criador do beneficiário"
						>
							<Input
								placeholder="Nome ou CPF / CNPJ"
								prefix={<BsPerson />}
							/>
						</FiltersFormItem>
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ width: '100%' }} align="middle">
					<Col span={24}>
						<FiltersFormItem
							name="created_at_start_date"
							label="Data de cadastro"
						>
							<CustomRangePicker
								maxDate={dayjs()}
								handleDateChange={dates => {
									form.setFieldsValue({
										created_at_start_date:
											dates?.[0]?.format('YYYY-MM-DD'),
										created_at_end_date:
											dates?.[1]?.format('YYYY-MM-DD'),
									});
								}}
							/>
						</FiltersFormItem>
						<FiltersFormItem
							style={{ display: 'none' }} // hidden field but necessary for form validation and set value
							name="created_at_end_date"
						/>
					</Col>
				</Row>
			</FiltersForm>
		</CustomDrawer>
	);
};

export default EscrowDetailsAccountDestinationFilters;
